import {
  HttpClient,
  HttpHeaders,
} from '@angular/common/http';
import {
  ElementRef,
  Injectable, OnDestroy,
} from '@angular/core';
import {
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';

import * as moment from 'moment';
import {
  BehaviorSubject,
  Observable,
  of,
  Subject,
} from 'rxjs';
import {
  filter,
  map, startWith,
  switchMap,
  take, takeUntil,
  tap,
} from 'rxjs/operators';

import { environment } from '../../../../environments/environment';
import {AppUser, AuthService} from '../../../core/modules/auth/services/auth.service';
import {
  Customer,
  CustomerService,
} from '../../../core/modules/auth/services/customer.service';
import { HelpDialogComponent } from '../components/help-dialog/help-dialog.component';
import {
  License,
  LicenseState,
  LocalOverride,
} from './licensing.interfaces';
import { testResponse } from './testLicenseResponse';
import {AngularFirestore} from '@angular/fire/compat/firestore';
import {LicenseSyncResponse} from '../models/licensing.models';

export interface LicenseResponse {
  success: boolean;
  response: License;
}

const DEFAULT_LICENSE_STATE = {
  licensed: false,
  loaded: false,
  bundle: null
};

@Injectable()
export class LittlesisLicensingService implements OnDestroy {

  static notAvailableMessage = 'This action is not available with your current license.';
  static trialTierJobLimit = 1;
  static limitWarningText = 'Due to your license being expired, <strong>all Roster and Guardian Sync automation has stopped</strong>.<br/>You can still perform <strong>manual analysis</strong>, but you will not be able to action changes <strong>until your license is renewed</strong>.';
  license = {} as License;
  originalLicense: string;
  overrideStatus$ = new BehaviorSubject(false);

  localOverride = {
    subscriptions: {}
  } as LocalOverride;

  validLicenseTypes = [
    'paid',
    'trial',
    'lapsed',
    'consultant',
    'unlicensed'
  ];

  private triggerForcedRecheck$: Subject<void> = new Subject<void>();

  private license$ = new BehaviorSubject<LicenseState>({...DEFAULT_LICENSE_STATE});

  private localOverride$ = new BehaviorSubject<LocalOverride>({subscriptions: {}});
  private httpOptions: { headers: HttpHeaders };
  private onDestroy$: Subject<void> = new Subject<void>();
  private lastEntitlementCheck: Date;


  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private customerService: CustomerService,
    public dialog: MatDialog,
    private angularFirestore: AngularFirestore
  ) {}

  triggerEntitlementRefresh(): Observable<boolean> {
    return this.authService.appUser$.pipe(
      filter(appUser => !!appUser.customer.customerId),
      switchMap((appUser: AppUser) =>
        this.angularFirestore
          .doc<LicenseSyncResponse>(`/customers/${appUser.customer.customerId}/entitlements/licenseSync`)
          .valueChanges()
      ),
      filter((resp: LicenseSyncResponse) => !!resp),
      map((resp: LicenseSyncResponse) => {
          if (resp.lastSyncRequest) {
            let needsRefresh = true;
            if (this.lastEntitlementCheck) {
              needsRefresh = this.lastEntitlementCheck?.getTime() !== resp.lastSyncRequest?.toDate()?.getTime();
            }
            if(needsRefresh) {
              this.license$.next({...DEFAULT_LICENSE_STATE});
              this.lastEntitlementCheck = resp.lastSyncRequest.toDate();
              return true;
            }
          }
          return false;
        }
      ));
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(null);
  }

  triggerForcedRecheck(): void{
    this.triggerForcedRecheck$.next(null);
  }

  getForcedRecheckTrigger(): Observable<void> {
    return this.triggerForcedRecheck$.asObservable();
  }

  getOverrideStatus(): Observable<boolean> {
    return this.overrideStatus$.asObservable();
  }

  setLocalOverride(tool, planName, expiry): void {

    this.localOverride = {
      subscriptions: {
        [tool]: {
          plan: planName,
          expiry
        }
      }
    };
    this.overrideStatus$.next(true);

    this.localOverride$.next({
      subscriptions: {
        [tool]: {
          plan: {
            name: planName
          },
          expiry
        }
      }
    });
  }

  fetchLicense(): Observable<LicenseResponse> {
    return this.customerService.getCustomer().pipe(
      filter(customer => !!customer),
      take(1),switchMap((customer: Customer) => {
        const url = environment.urls.services.licensing;
        const body = {
          domain: customer.domain,
          tools: environment.licensing.toolLicenseKeys
      };
        return (environment.licensing.useTestResponse) ?
          of(testResponse).pipe(tap((d) => console.log('fetchLicense test data'))) :
          this.http.post<LicenseResponse>(url, body, this.httpOptions).pipe(tap((d) => console.log('fetchLicense live data')));
      }));
  }

  mergeOverride(license: License): Observable<License>{
    return this.localOverride$.asObservable().pipe(map((override: LocalOverride) => {
      Object.entries(override).forEach(([tool, value]) => {
        if (license.subscriptions.hasOwnProperty(tool)) {
          license.subscriptions[tool].plan.name = value.plan.name;
           license.subscriptions[tool].expiry = value.expiry;
        }
      });
      return license;
    }));
  }

  checkLicense(forceRefresh: boolean = false): Observable<LicenseState> {
    return this.triggerEntitlementRefresh().pipe(
      startWith(true),
      filter((refresh: boolean) => refresh === true),
      switchMap(() => {
        if (!forceRefresh) {
          return this.license$.asObservable().pipe(
            switchMap(licenseState => {
              if (licenseState.loaded === true) {
                // cached license check for overrides
                return this.mergeOverride(licenseState.license)
                  .pipe(switchMap(license => {
                    const state = this.buildState(license);
                    return of(state);
                  }));
              } else {
                return this.checkLicense(true);
              }
            }));
        }
        return this.fetchLicense().pipe(
          switchMap((response: LicenseResponse) => this.mergeOverride(response.response)),
          switchMap((license: License) => {
            const state = this.buildState(license);
            this.license$.next(state);
            return this.license$.asObservable();
          })
        );
      })
    );
  }

  buildState(licenseResponse: License): LicenseState {
    const now = moment();
    const license: License = licenseResponse;
    const state: LicenseState = {
      licensed: false,
      loaded: true,
      license,
      bundle: null
    };

    let tags = [
      'Little SIS Premium'
    ];

    state.bundle = license.subscriptions?.['ait-lsp'].bundle;

    if (license.subscriptions['ait-lsp'].plan.name) {
      state.tier = license.subscriptions['ait-lsp'].plan.name as 'paid' | 'lapsed' | 'trial' | 'unlicensed';
    }

    if (license.subscriptions['ait-lsp'].expiry) {
      const expires = moment(license.subscriptions['ait-lsp'].expiry).endOf('d');
      let licenseDiff = expires.diff(now, 'd');

      // lowercase the license to ensure consistency and reduce
      license.subscriptions['ait-lsp'].plan['name'] = license.subscriptions['ait-lsp'].plan['name'].toLowerCase();

      // Fixes if license is less then full day expired
      if (expires.diff(now, 'h') < 0  && (expires.diff(now, 'h') > -24 )) {
        licenseDiff = -1;
      }

      state.licensed = this.validLicenseTypes.indexOf(license.subscriptions['ait-lsp'].plan.name) > -1;
      state.expiresIn = licenseDiff;

      if (licenseDiff >= 0) {
        state.details = 'ACTIVE';
      } else {
        state.details = 'EXPIRED';
      }

      tags = [...tags,
        license.subscriptions['ait-lsp'].plan.name,
        `Expiry: ${license.subscriptions['ait-lsp'].expiry}`];

    } else {
      state.licensed = (this.validLicenseTypes.indexOf(license.subscriptions['ait-lsp'].plan.name) > -1);
      tags = [...tags,
        license.subscriptions['ait-lsp'].plan.name,
        `Expiry: ${license.subscriptions['ait-lsp'].expiry}`];
    }

    if (!state.licensed) {
      if (license.subscriptions['ait-lsp'].plan.name === 'Unlicensed') {
        state.details = 'NEVER';
      } else {
        state.details = 'EXPIRED';
      }

    } else {
      state.tier = license.subscriptions['ait-lsp'].plan.name;
      if (state.tier === 'lapsed') {
        state.lastTierExpiration =
          {
            type: 'paid',
            date: license.subscriptions['ait-lsp'].plan.paid_ended_at
          };
      }

      if (state.tier === 'free') {
        state.lastTierExpiration = (license.subscriptions['ait-lsp'].plan.paid_ended_at)
          ? {date: license.subscriptions['ait-lsp'].plan.paid_ended_at, type: 'paid'}
          : {date: license.subscriptions['ait-lsp'].plan.trial_ended_at, type: 'trial'};
      }
    }
    // this.zendeskWebwidgetService.setChatTags(tags);
    return state;
  }

  openHelpDialog({ positionRelativeToElement,
                   hasBackdrop = false, height = '400px', width = '450px'}: {
    positionRelativeToElement: ElementRef;
    hasBackdrop?: boolean;
    height?: string;
    width?: string;
  }): MatDialogRef<any> {

    return this.dialog.open(HelpDialogComponent, {
      panelClass: 'ait-tight-panel',
      hasBackdrop,
      height,
      width,
      data: { positionRelativeToElement }
    });
  }

}
